import * as yup from 'yup';

/**
 * Yup schema for the unified company profile response returned by the sourcing
 * service's `GET /api/v1/company-profile` endpoint.
 *
 * The backend merges Crunchbase / Harmonic / PitchBook rows into an 11-section
 * profile (see `profile-builder.ts` in the sourcing API repo). Most nested
 * fields are optional and source-dependent, so we validate the top-level
 * shape strictly and keep the deeply nested payloads as loose records — the
 * adapter layer (`companyProfileAdapter.ts`) is responsible for safely
 * navigating and coercing them into the UI view-model.
 *
 * Refs:
 *  - sourcing/packages/contracts/src/schemas/company-profile.schema.ts
 *  - sourcing/services/api/src/company-profile/profile-builder.ts
 */

// A source-merged record of unknown keys. Kept loose on purpose — the adapter
// is the single source of truth for field coercions and defaults.
const looseRecord = yup
  .object()
  .nullable()
  .default(null)
  .test('is-record', 'Expected object or null', (val) => val === null || typeof val === 'object');

// Identity section — the only subfield we use directly as a strong string is
// `name`; everything else may be missing depending on source coverage.
const identitySectionSchema = yup
  .object({
    name: yup.string().nullable().default(null),
    legal_name: yup.string().nullable().default(null),
    also_known_as: yup.array(yup.string().required()).nullable().default(null),
    former_name: yup.string().nullable().default(null),
    short_description: yup.string().nullable().default(null),
    long_description: yup.string().nullable().default(null),
    external_description: yup.string().nullable().default(null),
    domain: yup.string().nullable().default(null),
    homepage_url: yup.string().nullable().default(null),
    logo_url: yup.string().nullable().default(null),
    year_founded: yup.mixed<number | string>().nullable().default(null),
    cb_url: yup.string().nullable().default(null),
    pb_url: yup.string().nullable().default(null),
    source_ids: yup
      .object({
        cb_uuid: yup.string().nullable().default(null),
        hm_id: yup.mixed<number | string>().nullable().default(null),
        hm_entity_urn: yup.string().nullable().default(null),
        pb_company_id: yup.string().nullable().default(null),
      })
      .nullable()
      .default(null),
  })
  .nullable()
  .default(null);

const locationSectionSchema = yup
  .object({
    city: yup.string().nullable().default(null),
    state: yup.string().nullable().default(null),
    country: yup.string().nullable().default(null),
    postal_code: yup.string().nullable().default(null),
    street_address: yup.string().nullable().default(null),
    address_formatted: yup.string().nullable().default(null),
  })
  .nullable()
  .default(null);

const statusSectionSchema = yup
  .object({
    operating_status: yup.string().nullable().default(null),
    ownership_status: yup.string().nullable().default(null),
    financing_status: yup.string().nullable().default(null),
    company_type: yup.string().nullable().default(null),
    universe: yup.string().nullable().default(null),
    exchange: yup.string().nullable().default(null),
    ticker: yup.string().nullable().default(null),
  })
  .nullable()
  .default(null);

const classificationSectionSchema = yup
  .object({
    industry: yup.mixed<string | string[]>().nullable().default(null),
    sub_industry: yup.array(yup.string().required()).nullable().default(null),
    keywords: yup.mixed<string | string[]>().nullable().default(null),
    technology_tags: yup.array(yup.string().required()).nullable().default(null),
    customer_type: yup.string().nullable().default(null),
    customer_type_tags: yup.array(yup.string().required()).nullable().default(null),
    product_type: yup.array(yup.string().required()).nullable().default(null),
    // Harmonic `highlights` may be an array of `{ text, category }` objects or
    // a pre-flattened list of strings — kept loose.
    highlights: yup.mixed().nullable().default(null),
  })
  .nullable()
  .default(null);

// Funding / headcount / people / social_presence / contact / traction /
// corporate_structure carry rich nested shapes that vary per source. We keep
// them as loose records so the adapter can coerce what it needs without
// failing validation on fields we don't consume.
//
// Top-level flat fields (`name`, `domain`, `industry`, etc.) mirror the
// current BE shape — the API now returns a denormalised profile alongside
// the legacy nested sections. `buildAddCompanyPayload` reads exclusively
// from this flat shape; the nested sections remain for the profile page
// adapter until that migration lands.
export const companyProfileSchema = yup
  .object({
    name: yup.string().nullable().default(null),
    domain: yup.string().nullable().default(null),
    website: yup.string().nullable().default(null),
    logoUrl: yup.string().nullable().default(null),
    industry: yup.string().nullable().default(null),
    sector: yup.string().nullable().default(null),
    headcount: yup.mixed<string | number | Record<string, unknown>>().nullable().default(null),
    foundedYear: yup.number().nullable().default(null),
    legalName: yup.string().nullable().default(null),
    linkedinUrl: yup.string().nullable().default(null),
    twitterUrl: yup.string().nullable().default(null),
    facebookUrl: yup.string().nullable().default(null),
    crunchbaseUrl: yup.string().nullable().default(null),
    shortDescription: yup.string().nullable().default(null),
    longDescription: yup.string().nullable().default(null),
    primaryLocation: yup
      .object({
        city: yup.string().nullable().default(null),
        state: yup.string().nullable().default(null),
        country: yup.string().nullable().default(null),
        zipCode: yup.string().nullable().default(null),
      })
      .nullable()
      .default(null),
    identity: identitySectionSchema,
    location: locationSectionSchema,
    status: statusSectionSchema,
    classification: classificationSectionSchema,
    funding: looseRecord,
    people: yup.array().nullable().default(null),
    social_presence: looseRecord,
    contact: looseRecord,
    traction: looseRecord,
    corporate_structure: looseRecord,
  })
  .required();

export const companyProfileResponseSchema = yup
  .object({
    profile: companyProfileSchema,
    sources: yup
      .object({
        crunchbase: looseRecord,
        harmonic: looseRecord,
        pitchbook: looseRecord,
      })
      .required(),
    meta: yup
      .object({
        sourceCoverage: yup.number().nullable().default(null),
        timingMs: yup.number().nullable().default(null),
        lookupKey: yup.mixed().nullable().default(null),
        fieldSources: yup.mixed().nullable().default(null),
      })
      .required(),
  })
  .required();

export type ICompanyProfileResponse = yup.InferType<typeof companyProfileResponseSchema>;
export type ICompanyProfile = yup.InferType<typeof companyProfileSchema>;

// ─── Recent News ────────────────────────────────────────────────────────────

/**
 * Yup mirror of the sourcing contract `CompanyNewsItemSchema` returned by
 * `GET /api/v1/company-profile/news`. Mirrors the existing
 * `ISourcingNewsItem` (used by the grid detail panel) so the same
 * `RecentNewsSection` component can render either source.
 */
const companyNewsItemSchema = yup.object({
  source: yup.string().required(),
  date: yup.string().required(),
  title: yup.string().required(),
  url: yup.string().required(),
});

export const companyNewsResponseSchema = yup
  .object({
    news: yup.array(companyNewsItemSchema.required()).required(),
    meta: yup
      .object({
        timingMs: yup.number().nullable().default(null),
        lookupKey: yup.mixed().nullable().default(null),
      })
      .nullable()
      .default(null),
  })
  .required();

export type ICompanyNewsItem = yup.InferType<typeof companyNewsItemSchema>;
export type ICompanyNewsResponse = yup.InferType<typeof companyNewsResponseSchema>;

// ─── Foresight AI Summary ──────────────────────────────────────────────────

/**
 * Yup mirror of the sourcing contract `CompanySummaryResponseSchema`
 * returned by `GET /api/v1/company-profile/summary`. The frontend renders
 * each entry as a bullet point in `ForesightSummaryPanel`.
 */
export const companySummaryResponseSchema = yup
  .object({
    summary: yup.array(yup.string().required()).required(),
    generatedAt: yup.string().nullable().default(null),
    meta: yup
      .object({
        timingMs: yup.number().nullable().default(null),
        lookupKey: yup.mixed().nullable().default(null),
      })
      .nullable()
      .default(null),
  })
  .required();

export type ICompanySummaryResponse = yup.InferType<typeof companySummaryResponseSchema>;

// ─── Company Lists Lookup ──────────────────────────────────────────────────

/**
 * Yup mirror of the sourcing contract `CompanyListsResponseSchema`
 * returned by `GET /api/v1/company-profile/lists`. Lets the UI ask "does
 * this company already belong to any of my saved lists?" so we can:
 *  - render a live `Added to N lists` count on the profile header, and
 *  - hide already-saved lists from the "Add to list" picker so the user
 *    can't add a duplicate.
 *
 * Per the integration guide, a Snowflake failure during indirect
 * resolution is downgraded server-side to `200` with `lists: []`, so
 * callers don't need to special-case errors — an empty list is a valid
 * "no matches" or "could not resolve" state.
 */
const companyListEntrySchema = yup.object({
  listId: yup.string().required(),
  shortName: yup.string().required(),
  entityType: yup.string().required(),
  type: yup.string().required(),
  folderId: yup.string().nullable().default(null),
  visibility: yup.string().required(),
  itemCount: yup.number().required(),
  listCompanyId: yup.string().required(),
  matchedBy: yup.string().required(),
  addedAt: yup.string().required(),
});

const companyListsResolvedIdentifiersSchema = yup
  .object({
    domain: yup.string().nullable().default(null),
    linkedinUrl: yup.string().nullable().default(null),
  })
  .nullable()
  .default(null);

export const companyListsResponseSchema = yup
  .object({
    lists: yup.array(companyListEntrySchema.required()).required(),
    meta: yup
      .object({
        resolvedVia: yup.string().nullable().default(null),
        lookupKey: yup.mixed().nullable().default(null),
        resolvedIdentifiers: companyListsResolvedIdentifiersSchema,
        timingMs: yup.number().nullable().default(null),
      })
      .required(),
  })
  .required();

export type ICompanyListEntry = yup.InferType<typeof companyListEntrySchema>;
export type ICompanyListsResponse = yup.InferType<typeof companyListsResponseSchema>;
